import {
  IntegrationConnectionResource,
  IntegrationConnectionType,
  IntegrationConnectionTypes,
} from "@arrowsup/platform-dtos";
import CableIcon from "@mui/icons-material/Cable";
import {
  BulkDeleteButton,
  Create,
  Datagrid,
  Edit,
  FunctionField,
  List,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { useVerifyIntegrationConnection } from "../../logic/connected-accounts/integration-connections.service";
import { CrudPermissions } from "../auth/crud-permissions";
import { TopHelpInfoText } from "../help/top-help-info-text";
import {
  ApiKeyInput,
  connectedAccountChanges,
  connectedAccountStatusColumns,
  ConnectedAccountStatusInput,
  connectedAccountTypeChoices,
  ReplaceApiKeyInput,
  VerifyAccessButton,
} from "./connected-account-fields";

const noun = "connection";

const typeChoices = connectedAccountTypeChoices(IntegrationConnectionTypes);

/** Where each vendor's key comes from, for people adding one. */
export const integrationConnectionKeyHelp: Record<
  IntegrationConnectionType,
  string
> = {
  dialpad: "A company admin API key from Dialpad.",
  leap: "An access token from Leap under Settings → Developer. Only Leap admin users can generate one.",
};

export const IntegrationConnectionResourceView = (perms: CrudPermissions) => (
  <Resource
    name="integration-connection"
    key="integration-connection"
    options={{ label: "Connections" }}
    list={<IntegrationConnectionList perms={perms} />}
    create={perms.create ? IntegrationConnectionCreate : undefined}
    edit={perms.update ? IntegrationConnectionEdit : undefined}
    icon={CableIcon}
  />
);

const IntegrationConnectionList: React.FC<{ perms: CrudPermissions }> = ({
  perms,
}) => (
  <>
    <TopHelpInfoText text="Dialpad and Leap accounts this account's input streams read through, by API key. Replace a key here and every stream using the connection picks it up." />
    <List>
      <Datagrid
        rowClick={perms.update ? "edit" : false}
        bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
      >
        <TextField source="name" />
        <FunctionField
          label="Type"
          render={(r: IntegrationConnectionResource) =>
            IntegrationConnectionTypes[r.type].label
          }
        />
        {connectedAccountStatusColumns()}
      </Datagrid>
    </List>
  </>
);

const NewKeyInput: React.FC = () => {
  const type = useWatch<{ type?: IntegrationConnectionType }>({
    name: "type",
  });
  return type ? (
    <ApiKeyInput
      helperText={`${integrationConnectionKeyHelp[type]} Arrows Up checks it works before saving.`}
    />
  ) : null;
};

export const IntegrationConnectionCreate: React.FC = () => (
  <Create redirect="list">
    <SimpleForm>
      <SelectInput source="type" choices={typeChoices} required fullWidth />
      <TextInput
        source="name"
        helperText="How streams name it, e.g. the company it belongs to. Unique per type in this account."
        required
        fullWidth
      />
      <NewKeyInput />
    </SimpleForm>
  </Create>
);

const IntegrationConnectionVerifyButton: React.FC = () => (
  <VerifyAccessButton noun={noun} verify={useVerifyIntegrationConnection()} />
);

export const IntegrationConnectionEdit: React.FC = () => (
  <Edit
    redirect="list"
    transform={(data: Record<string, unknown>) =>
      connectedAccountChanges(data, ["type"])
    }
  >
    <SimpleForm>
      <SelectInput source="type" choices={typeChoices} disabled fullWidth />
      <TextInput source="name" required fullWidth />
      <ReplaceApiKeyInput noun={noun} />
      <ConnectedAccountStatusInput whenDisabled="Disabled connections pause the input streams that use them." />
      <IntegrationConnectionVerifyButton />
    </SimpleForm>
  </Edit>
);
